import { ApiError, ErrorCode } from '@approvalmax/types';
import { toast } from '@approvalmax/ui/src/components';
import { errorHelpers } from '@approvalmax/utils';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { QueryKeys, selectors } from 'modules/common';
import { backend, schemas, stateTree } from 'modules/data';
import { normalize } from 'normalizr';
import { useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { api } from 'services/api';
import {
    useGetAllOpenRequestsWithParticipantDecisionRequired,
    useGetAllParticipantWorkflowVersions,
    useGetQuantityOfAllOpenRequestsWithParticipantDecisionRequired,
    useReplacementOffboardParticipantWithAllRequests,
} from 'shared/data/webApp/v1';
import { useGetCompanyDelegations } from 'shared/data/webApp/v2';

import { cancelActivePopup, loadPageData } from '../../actions';
import { OffboardUserPopupData } from '../../reducers/page/activePopupReducer';
import { getActivePopup } from '../../selectors/pageSelectors';
import { useOffboardingContext } from './OffboardingContext';
import {
    getRequestReplacementId,
    getWorkflowAutoApproveStepId,
    getWorkflowReplacementId,
} from './OffboardUserPopupContent.helpers';
import { messages } from './OffboardUserPopupContent.messages';
import {
    GLOBAL_USER_REPLACEMENT,
    OffboardUserInfoAnswer,
    RequestReplacementMode,
    Row,
} from './OffboardUserPopupContent.types';

export const useOffboardUserQuery = () => {
    const offboardingUser = useSelector((state: stateTree.State) => {
        const email = getActivePopup<OffboardUserPopupData>(state).userEmail;

        return selectors.user.getUserById(state, email);
    });
    const company = useSelector(selectors.navigation.getActiveCompany);

    const requestParams = useMemo(
        () => ({
            query: {
                companyId: company.id,
                userId: offboardingUser!.databaseId!,
            },
        }),
        [company.id, offboardingUser]
    );

    const {
        data: allParticipantWorkflowVersions,
        isInitialLoading: isLoadingAllParticipantWorkflowVersions,
        isFetching: isFetchingAllParticipantWorkflowVersions,
        isError: isErrorAllParticipantWorkflowVersions,
    } = useGetAllParticipantWorkflowVersions(requestParams);

    const {
        data: allOpenRequestsWithParticipantDecisionRequired,
        isInitialLoading: isLoadingAllOpenRequestsWithParticipantDecisionRequired,
        isFetching: isFetchingAllOpenRequestsWithParticipantDecisionRequired,
        isError: isErrorAllOpenRequestsWithParticipantDecisionRequired,
    } = useGetAllOpenRequestsWithParticipantDecisionRequired(requestParams, {
        enabled: !company.flags.isWorkflowManager,
    });

    const {
        data: quantityOfAllOpenRequestsWithParticipantDecisionRequired,
        isInitialLoading: isLoadingQuantityOfAllOpenRequestsWithParticipantDecisionRequired,
        isFetching: isFetchingQuantityOfAllOpenRequestsWithParticipantDecisionRequired,
        isError: isErrorQuantityOfAllOpenRequestsWithParticipantDecisionRequired,
    } = useGetQuantityOfAllOpenRequestsWithParticipantDecisionRequired(requestParams, {
        enabled: company.flags.isWorkflowManager,
    });

    if (company.flags.isWorkflowManager) {
        return {
            isInitialLoading:
                isLoadingAllParticipantWorkflowVersions ||
                isLoadingQuantityOfAllOpenRequestsWithParticipantDecisionRequired,
            data: {
                requests: [],
                workflows: allParticipantWorkflowVersions?.workflowVersions ?? [],
                isWorkflowManager: true,
                quantityRequests: quantityOfAllOpenRequestsWithParticipantDecisionRequired?.quantity,
            },
            isFetching:
                isFetchingAllParticipantWorkflowVersions ||
                isFetchingQuantityOfAllOpenRequestsWithParticipantDecisionRequired,
            isError:
                isErrorAllParticipantWorkflowVersions ||
                isErrorQuantityOfAllOpenRequestsWithParticipantDecisionRequired,
        };
    } else {
        return {
            isInitialLoading:
                isLoadingAllParticipantWorkflowVersions || isLoadingAllOpenRequestsWithParticipantDecisionRequired,
            data: {
                requests: allOpenRequestsWithParticipantDecisionRequired?.requests ?? [],
                workflows: allParticipantWorkflowVersions?.workflowVersions ?? [],
            },
            isFetching:
                isFetchingAllParticipantWorkflowVersions || isFetchingAllOpenRequestsWithParticipantDecisionRequired,
            isError: isErrorAllParticipantWorkflowVersions || isErrorAllOpenRequestsWithParticipantDecisionRequired,
        };
    }
};

export const useOffboardUserMutation = (
    userIdToOffboard: string,
    offboardUserInfo: OffboardUserInfoAnswer,
    companyId: string,
    isWorkflowManager: boolean
) => {
    const { selectedReplacements, requestReplacementMode } = useOffboardingContext();

    const { refetch: getCompanyDelegations } = useGetCompanyDelegations({
        path: { companyId },
    });

    const dispatch = useDispatch();
    const queryClient = useQueryClient();
    const onError = useCallback(
        (error: ApiError) => {
            switch (error.code) {
                case ErrorCode.E4142_OUTDATED_OFFBOARD_DATA:
                    queryClient.invalidateQueries([QueryKeys.OFFBOARDING_USER_CONTEXT, userIdToOffboard]);
                    break;

                case ErrorCode.E4036_STEPS_MUST_BE_SPECIFIED:
                    queryClient.invalidateQueries([QueryKeys.OFFBOARDING_USER_CONTEXT, userIdToOffboard]);
                    break;
            }
        },
        [queryClient, userIdToOffboard]
    );

    const onSuccess = useCallback(async () => {
        const entities = await api.companies.select({ companyId }).then((result: any) => {
            const entities = normalize(result, { Companies: [schemas.companySchemaLegacy] }).entities;

            if (!entities.companies || !entities.companies[companyId]) {
                throw errorHelpers.notFoundError();
            }

            return entities;
        });

        await getCompanyDelegations();

        dispatch(loadPageData(companyId, entities));
        dispatch(cancelActivePopup());
        toast.success(messages.successfulOffboarding);
    }, [companyId, dispatch, getCompanyDelegations]);

    const workflows = useMemo(
        () =>
            offboardUserInfo.workflows.map((workflow) => {
                const autoApprovalId = getWorkflowAutoApproveStepId(workflow);

                let autoApprovalStepReplacementUserId;

                const replacementForAutoApproveStep = selectedReplacements[autoApprovalId];

                if (replacementForAutoApproveStep) {
                    autoApprovalStepReplacementUserId = replacementForAutoApproveStep.databaseId;
                } else if (workflow.hasRequesterInAutoApprovalRules) {
                    errorHelpers.invalidOperationError('Replacement is required for auto-approve step');
                }

                return {
                    workflowId: workflow.workflowId,
                    version: workflow.version,
                    ...(autoApprovalStepReplacementUserId && {
                        autoApprovalStepReplacementUserId,
                    }),
                    replacements: workflow.replacements.map((replacement) => {
                        const uniqStepId = getWorkflowReplacementId(replacement, workflow);

                        return {
                            stepNumber: replacement.stepNumber,
                            userId: selectedReplacements[uniqStepId]?.databaseId,
                            role: replacement.role,
                        };
                    }),
                };
            }),
        [offboardUserInfo, selectedReplacements]
    );

    const mutationWithRequests = useMutation<backend.BaseAnswer, ApiError>(
        () => {
            const transfer = {
                companyId,
                userIdToOffboard,
                workflows,
                requests: offboardUserInfo.requests.map((request) => ({
                    requestId: request.requestId,
                    version: request.version,
                    replacements: request.replacements.map((replacement) => {
                        const uniqStepId = getRequestReplacementId(replacement, request);

                        return {
                            stepNumber: replacement.stepNumber,
                            role: replacement.role,
                            userId:
                                requestReplacementMode === RequestReplacementMode.OneForAll
                                    ? selectedReplacements[GLOBAL_USER_REPLACEMENT]?.databaseId
                                    : selectedReplacements[uniqStepId]?.databaseId,
                        };
                    }),
                })),
            };

            return api.companies.offboardParticipant(transfer);
        },
        {
            onError,
            onSuccess,
        }
    );

    const {
        mutate: replacementOffboardParticipantWithAllRequests,
        isLoading: isLoadingReplacementOffboardParticipantWithAllRequests,
    } = useReplacementOffboardParticipantWithAllRequests({
        onError,
        onSuccess,
    });

    const mutateWorkflowManager = useCallback(
        () =>
            replacementOffboardParticipantWithAllRequests({
                body: {
                    companyId,
                    userIdToOffboard,
                    workflows,
                    userIdForRequestsReplacement: selectedReplacements[GLOBAL_USER_REPLACEMENT]?.databaseId,
                },
            }),
        [companyId, replacementOffboardParticipantWithAllRequests, selectedReplacements, userIdToOffboard, workflows]
    );

    if (isWorkflowManager) {
        return {
            mutate: mutateWorkflowManager,
            isLoading: isLoadingReplacementOffboardParticipantWithAllRequests,
        };
    } else {
        return mutationWithRequests;
    }
};

export const useGetInvalidItems = (data: Row[]) => {
    const { showErrors, selectedReplacements } = useOffboardingContext();

    return useMemo(() => {
        return data
            .filter((item) => item.required && showErrors && !selectedReplacements[item.id])
            .map((item) => item.id);
    }, [data, selectedReplacements, showErrors]);
};
